import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Upload, GitCompareArrows, FileText, Bell, ArrowUpRight } from "lucide-react";

/**
 * QENVA · Quick Actions — 4 utility cards at the bottom of the Dashboard.
 *
 *   Ajouter un document  |  Lancer CrossCheck™  |  Générer un rapport  |  Créer une alerte
 */
function buildActions(t) {
  return [
    {
      key: "upload", icon: Upload, label: t("quick_actions.upload_label"),
      description: t("quick_actions.upload_desc"),
      accent: "text-qenva-blue", bg: "bg-qenva-blue/10", route: "/upload",
    },
    {
      key: "crosscheck", icon: GitCompareArrows, label: t("quick_actions.crosscheck_label"),
      description: t("quick_actions.crosscheck_desc"),
      accent: "text-emerald-600", bg: "bg-emerald-50", route: "/comparisons",
    },
    {
      key: "report", icon: FileText, label: t("quick_actions.report_label"),
      description: t("quick_actions.report_desc"),
      accent: "text-[#8B5CF6]", bg: "bg-[#8B5CF6]/10", route: "/dashboard",
      customAction: "exec-report",
    },
    {
      key: "alert", icon: Bell, label: t("quick_actions.alert_label"),
      description: t("quick_actions.alert_desc"),
      accent: "text-amber-600", bg: "bg-amber-50", route: "/alerts?new=1",
    },
  ];
}

export default function QuickActions({ onCustomAction, testId = "quick-actions" }) {
  const { t } = useTranslation("dashboard");
  const navigate = useNavigate();
  const ACTIONS = buildActions(t);
  return (
    <section data-testid={testId}>
      <header className="mb-3">
        <p className="text-[10.5px] uppercase tracking-[0.16em] font-bold text-zinc-500">{t("quick_actions.title")}</p>
      </header>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {ACTIONS.map((a) => {
          const Icon = a.icon;
          const handle = () => {
            if (a.customAction && onCustomAction) onCustomAction(a.customAction);
            else navigate(a.route);
          };
          return (
            <button
              key={a.key}
              type="button"
              onClick={handle}
              data-testid={`${testId}-${a.key}`}
              className="text-left rounded-xl border border-zinc-200 bg-white p-4 hover:border-qenva-blue/30 hover:shadow-sm transition-all flex items-center gap-3"
            >
              <div className={`w-10 h-10 rounded-lg grid place-items-center shrink-0 ${a.bg} ${a.accent}`}>
                <Icon size={17} strokeWidth={1.75} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display font-bold text-qenva-navy text-[13px] tracking-tight truncate">{a.label}</p>
                <p className="text-[11px] text-zinc-500 truncate">{a.description}</p>
              </div>
              <ArrowUpRight size={13} className="text-zinc-300 shrink-0" />
            </button>
          );
        })}
      </div>
    </section>
  );
}
